import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  ProgressBar,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  surface: {
    width: "min(760px, calc(100vw - 32px))",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  subTitle: {
    color: tokens.colorNeutralForeground3,
  },
  content: {
    minHeight: "220px",
    paddingTop: "8px",
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: "12px",
  },
  stepLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  stepRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
});

function normalizePages(pages) {
  return Array.isArray(pages) ? pages.filter(Boolean) : [];
}

export default function Wizard({
  isOpen,
  setIsOpen,
  data,
  setData,
  pages,
  title = "Wizard",
  headerActions,
  onCancel,
  onFinish,
  finishLabel,
  cancelLabel,
  backLabel,
  nextLabel,
}) {
  const styles = useStyles();
  const normalizedPages = useMemo(() => normalizePages(pages), [pages]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [validationError, setValidationError] = useState("");

  const pageCount = normalizedPages.length;
  const page = normalizedPages[currentPageIndex] || null;
  const isFirstPage = currentPageIndex === 0;
  const isLastPage = currentPageIndex === pageCount - 1;

  useEffect(() => {
    if (isOpen) {
      setCurrentPageIndex(0);
      setValidationError("");
    }
  }, [isOpen]);

  const closeWizard = () => {
    setValidationError("");
    setIsOpen?.(false);
    onCancel?.(data);
  };

  const updateData = (patch) => {
    if (!setData) return;
    if (typeof patch === "function") {
      setData((prev) => patch(prev));
      return;
    }
    setData((prev) => ({ ...prev, ...patch }));
  };

  const runValidation = async () => {
    if (!page?.validate) return true;
    const result = await page.validate(data);
    if (result === true || result == null) {
      setValidationError("");
      return true;
    }
    const message = typeof result === "string" ? result : "Please complete all required fields.";
    setValidationError(message);
    return false;
  };

  const handleBack = async () => {
    setValidationError("");
    await page?.onPrevious?.({ data, updateData, pageIndex: currentPageIndex });
    setCurrentPageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = async () => {
    const valid = await runValidation();
    if (!valid) return;

    await page?.onNext?.({ data, updateData, pageIndex: currentPageIndex });

    if (isLastPage) {
      await onFinish?.(data);
      setIsOpen?.(false);
      return;
    }

    setValidationError("");
    setCurrentPageIndex((prev) => Math.min(pageCount - 1, prev + 1));
  };

  const progressValue = pageCount <= 1 ? 1 : (currentPageIndex + 1) / pageCount;
  const { t } = useTranslation();

  return (
    <Dialog
      open={Boolean(isOpen)}
      onOpenChange={(_, dialogData) => {
        if (!dialogData.open) closeWizard();
      }}
      modalType="modal"
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {page ? (
              <div className={styles.header}>
                <div className={styles.stepLabel}>
                  <Text weight="semibold">{page.title || `Step ${currentPageIndex + 1}`}</Text>
                  <div className={styles.stepRight}>
                    {headerActions || null}
                    <Text className={styles.subTitle}>
                      {currentPageIndex + 1} / {pageCount}
                    </Text>
                  </div>
                </div>
                <ProgressBar value={progressValue} thickness="large" />
                {page.description ? <Text className={styles.subTitle}>{page.description}</Text> : null}
              </div>
            ) : null}

            <div className={styles.content}>
              {page ? (
                page.render?.({ data, updateData, pageIndex: currentPageIndex })
              ) : (
                <Text>No pages provided.</Text>
              )}
              {validationError ? <Text className={styles.error}>{validationError}</Text> : null}
            </div>
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={closeWizard}>
              {cancelLabel}
            </Button>
            <Button appearance="subtle" disabled={isFirstPage || pageCount === 0} onClick={handleBack}>
              {page?.backLabel || backLabel}
            </Button>
            <Button appearance="primary" disabled={pageCount === 0} onClick={handleNext}>
              {isLastPage ? page?.finishLabel || finishLabel : page?.nextLabel || nextLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
